import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Capture the backend request id on every response so failing requests can be
// traced in Railway logs without extra instrumentation. Successful responses
// expose it on `response.headers['x-request-id']` for callers that want it;
// failing ones get a console warning that includes it so it is one paste away.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestId = error?.response?.headers?.['x-request-id'];
    if (requestId) {
      // eslint-disable-next-line no-console
      console.warn(`[apollo] request failed request_id=${requestId} status=${error?.response?.status} url=${error?.config?.url}`);
    }
    return Promise.reject(error);
  },
);

// Format API error for display
export const formatApiError = (error) => {
  const detail = error?.response?.data?.detail;
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

// Organizations
export const createOrganization = (name) => api.post('/organizations', { name });
export const getOrganizations = () => api.get('/organizations');
export const switchOrganization = (orgId) => api.put(`/organizations/${orgId}/switch`);
export const getOrganizationSchedulingSettings = () => api.get('/organizations/current/scheduling-settings');
export const updateOrganizationSchedulingSettings = (settings) => api.put('/organizations/current/scheduling-settings', { settings });

// API Keys
export const createApiKey = (data) => api.post('/api-keys', data);
export const getApiKeys = () => api.get('/api-keys');
export const deleteApiKey = (keyId) => api.delete(`/api-keys/${keyId}`);
export const getIntegrationRegistry = () => api.get('/integrations/registry');
export const getIntegrationInstallations = () => api.get('/integrations/installations');
export const createIntegrationInstallation = (data) => api.post('/integrations/installations', data);
export const updateIntegrationInstallation = (installationId, data) => api.put(`/integrations/installations/${installationId}`, data);
export const runIntegrationHealthCheck = (installationId) => api.post(`/integrations/installations/${installationId}/health-check`);
export const executeIntegrationInstallationAction = (installationId, data) => api.post(`/integrations/installations/${installationId}/execute`, data);

// Provider Connections
export const createProviderConnection = (data) => api.post('/provider-connections', data);
export const getProviderConnections = () => api.get('/provider-connections');
export const updateProviderConnection = (connectionId, data) => api.put(`/provider-connections/${connectionId}`, data);
export const previewSchedulingAvailability = (data) => api.post('/scheduling/preview-availability', data);
export const createSchedulingBooking = (data) => api.post('/scheduling/create-booking', data);
export const rescheduleSchedulingBooking = (data) => api.post('/scheduling/reschedule-booking', data);
export const cancelSchedulingBooking = (data) => api.post('/scheduling/cancel-booking', data);
export const getSchedulingBookings = (params) => api.get('/scheduling/bookings', { params });
export const getSchedulingBooking = (bookingReference) => api.get(`/scheduling/bookings/${bookingReference}`);
export const startGoogleCalendarConnection = (data) => api.post('/provider-connections/google-calendar/start', data);
export const getGoogleCalendarOAuthStatus = () => api.get('/oauth/google/calendar/status');
export const disconnectGoogleCalendarOAuth = () => api.delete('/oauth/google/calendar/disconnect');
export const getGmailOAuthStatus = () => api.get('/oauth/google/gmail/status');
export const startGmailOAuthLogin = () => api.get('/oauth/google/gmail/login');
export const disconnectGmailOAuth = () => api.delete('/oauth/google/gmail/disconnect');
export const getGoogleDriveOAuthStatus = () => api.get('/oauth/google/drive/status');
export const startGoogleDriveOAuthLogin = () => api.get('/oauth/google/drive/login');
export const disconnectGoogleDriveOAuth = () => api.delete('/oauth/google/drive/disconnect');
export const getOutlookMailOAuthStatus = () => api.get('/oauth/microsoft/outlook-mail/status');
export const startOutlookMailOAuthLogin = () => api.get('/oauth/microsoft/outlook-mail/login');
export const disconnectOutlookMailOAuth = () => api.delete('/oauth/microsoft/outlook-mail/disconnect');
export const getGoogleMeetImportDependencies = (installationId) => api.get(`/integrations/google-meet-import/dependencies/${installationId}`);
export const discoverGoogleMeetImportArtifacts = (installationId, params) => api.post(`/integrations/google-meet-import/discover/${installationId}`, null, { params });
export const getGoogleMeetImportArtifacts = (installationId, params) => api.get(`/integrations/google-meet-import/artifacts/${installationId}`, { params });
export const getGoogleMeetImportRuns = (installationId, params) => api.get(`/integrations/google-meet-import/runs/${installationId}`, { params });
export const refreshGoogleMeetImportArtifactDetail = (artifactId) => api.post(`/integrations/google-meet-import/artifacts/${artifactId}/detail`);
export const updateGoogleMeetImportArtifactStatus = (artifactId, status) => api.post(`/integrations/google-meet-import/artifacts/${artifactId}/status`, null, { params: { status } });
export const getZoomOAuthStatus = (installationId) => api.get(`/integrations/zoom/oauth/status/${installationId}`);
export const startZoomOAuth = (installationId) => api.get(`/integrations/zoom/oauth/start/${installationId}`);
export const disconnectZoomOAuth = (installationId) => api.delete(`/integrations/zoom/oauth/disconnect/${installationId}`);

// Agents
export const createAgent = (data) => api.post('/agents', data);
export const getAgents = () => api.get('/agents');
export const getAgent = (agentId) => api.get(`/agents/${agentId}`);
export const updateAgent = (agentId, data) => api.put(`/agents/${agentId}`, data);
export const getAgentEditableFiles = (agentId) => api.get(`/agents/${agentId}/editable-files`);
export const updateAgentEditableFiles = (agentId, data) => api.put(`/agents/${agentId}/editable-files`, data);
export const uploadAgentAvatar = (agentId, file) => {
  const formData = new FormData();
  formData.append('avatar', file);
  return api.post(`/agents/${agentId}/avatar`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const deleteAgentAvatar = (agentId) => api.delete(`/agents/${agentId}/avatar`);
export const deleteAgent = (agentId) => api.delete(`/agents/${agentId}`);

// Conversations
export const getConversations = () => api.get('/conversations');
export const createConversation = (data) => api.post('/conversations', data);
export const sendMessage = (convId, content) => api.post(`/conversations/${convId}/messages`, { content, role: 'user' });
export const getMessages = (convId) => api.get(`/conversations/${convId}/messages`);
export const getConversationFeedback = (convId) => api.get(`/conversations/${convId}/feedback`);
export const createConversationFeedback = (convId, data) => api.post(`/conversations/${convId}/feedback`, data);
export const getFeedbackQueue = () => api.get('/feedback-queue');
export const updateFeedbackQueueItem = (feedbackId, data) => api.put(`/feedback-queue/${feedbackId}`, data);
export const routeFeedbackQueueItem = (feedbackId, data) => api.post(`/feedback-queue/${feedbackId}/route`, data);
export const getPromptImprovements = () => api.get('/prompt-improvements');
export const updatePromptImprovement = (itemId, data) => api.put(`/prompt-improvements/${itemId}`, data);
export const getKnowledgeSources = () => api.get('/knowledge-sources');

// Contacts
export const getContactProfiles = () => api.get('/contact-profiles');
export const getContactProfile = (profileId) => api.get(`/contact-profiles/${profileId}`);

// Twilio
export const createTwilioConfig = (data) => api.post('/twilio/config', data);
export const getTwilioConfig = () => api.get('/twilio/config');

// Analytics
export const getAnalyticsOverview = () => api.get('/analytics/overview');

// Runtime
export const getRuntimeVersion = () => api.get('/runtime/version');
export const getRuntimeOverview = () => api.get('/runtime/overview');
export const createRuntimeProject = (data) => api.post('/runtime/projects', data);
export const updateRuntimeProject = (projectId, data) => api.put(`/runtime/projects/${projectId}`, data);
export const getRuntimeProjects = () => api.get('/runtime/projects');
export const getRuntimeSecrets = () => api.get('/runtime/secrets');
export const createRuntimeSecret = (data) => api.post('/runtime/secrets', data);
export const deleteRuntimeSecret = (secretId) => api.delete(`/runtime/secrets/${secretId}`);
export const getRuntimeConnectors = () => api.get('/runtime/connectors');
export const getRuntimeConnector = (connectorId) => api.get(`/runtime/connectors/${connectorId}`);
export const createRuntimeConnector = (data) => api.post('/runtime/connectors', data);
export const updateRuntimeConnector = (connectorId, data) => api.put(`/runtime/connectors/${connectorId}`, data);
export const deleteRuntimeConnector = (connectorId) => api.delete(`/runtime/connectors/${connectorId}`);
export const createRuntimeBrowserSession = (taskId, data) => api.post(`/runtime/tasks/${taskId}/browser-sessions`, data);
export const getRuntimeBrowserSessions = (taskId) => api.get(`/runtime/tasks/${taskId}/browser-sessions`);
export const getRuntimeBrowserSession = (sessionId) => api.get(`/runtime/browser-sessions/${sessionId}`);
export const executeRuntimeBrowserAction = (taskId, sessionId, data) => api.post(`/runtime/tasks/${taskId}/browser-sessions/${sessionId}/actions`, data);
export const getRuntimeBrowserStoragePolicy = () => api.get('/runtime/browser/storage-policy');
export const cleanupRuntimeBrowserArtifacts = (maxArtifacts = 50) => api.post(`/runtime/browser/cleanup?maxArtifacts=${maxArtifacts}`);
export const getRuntimeArtifactContentUrl = (artifactId) => `${API}/runtime/artifacts/${artifactId}/content`;
export const createRuntimeTask = (data) => api.post('/runtime/tasks', data);
export const updateRuntimeTask = (taskId, data) => api.put(`/runtime/tasks/${taskId}`, data);
export const getRuntimeTasks = (params) => api.get('/runtime/tasks', { params });
export const getRuntimeTaskDetail = (taskId) => api.get(`/runtime/tasks/${taskId}/detail`);
export const getRuntimeWorkers = () => api.get('/runtime/workers');
export const getRuntimeBacklog = () => api.get('/runtime/orchestration/backlog');
export const getRuntimePlannerContext = (taskId) => api.get(`/runtime/tasks/${taskId}/planner-context`);
export const getRuntimeTaskInterventions = (taskId) => api.get(`/runtime/tasks/${taskId}/interventions`);
export const getRuntimeCronJobsOverview = () => api.get('/runtime/cron-jobs/overview');
export const getRuntimeCronJobs = () => api.get('/runtime/cron-jobs');
export const createRuntimeCronJob = (data) => api.post('/runtime/cron-jobs', data);
export const updateRuntimeCronJob = (jobId, data) => api.put(`/runtime/cron-jobs/${jobId}`, data);
export const pauseRuntimeCronJob = (jobId) => api.post(`/runtime/cron-jobs/${jobId}/pause`);
export const resumeRuntimeCronJob = (jobId) => api.post(`/runtime/cron-jobs/${jobId}/resume`);
export const runNowRuntimeCronJob = (jobId) => api.post(`/runtime/cron-jobs/${jobId}/run-now`);
export const deleteRuntimeCronJob = (jobId) => api.delete(`/runtime/cron-jobs/${jobId}`);
export const getRuntimeCronJobRuns = (jobId) => api.get(`/runtime/cron-jobs/${jobId}/runs`);
export const createRuntimeDirective = (taskId, data) => api.post(`/runtime/tasks/${taskId}/directives`, data);
export const uploadRuntimeAttachment = (file, agentId) => {
  const form = new FormData();
  form.append('file', file);
  if (agentId) form.append('agent_id', agentId);
  return api.post('/runtime/attachments', form, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const getRuntimeUserUpdates = (params) => api.get('/runtime/user-updates', { params });
export const createRuntimeUserUpdate = (data) => api.post('/runtime/user-updates', data);
export const flushStaleRuntimeTasks = (projectId) => api.post('/runtime/tasks/flush-stale', {}, { params: projectId ? { project_id: projectId } : {} });
export const hardResetRuntimeProject = (projectId) => api.post('/runtime/tasks/hard-reset', {}, { params: projectId ? { project_id: projectId } : {} });
export const decideRuntimeApproval = (approvalId, data) => api.post(`/runtime/approvals/${approvalId}/decision`, data);
export const decideRuntimeBlocker = (blockerId, data) => api.post(`/runtime/blockers/${blockerId}/decision`, data);

export default api;
